import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { BackofficeCompany, BackofficePlan } from './backoffice.models';
import { BackofficeService } from './backoffice.service';

type CompanyAdminPlanCard = {
  key: string;
  name: string;
  description: string;
  capabilityLabels: string[];
  restrictionLabels: string[];
  accessScope: string;
  total: number;
  used: number;
  remaining: number;
  unitPriceEuros: number | null;
  revenueEuros: number | null;
};

type CompanySubscriptionDetail = {
  key: string;
  name: string;
  seatCount: number;
};

type SuperadminPlanSubscription = {
  companyId: string;
  companyName: string;
  licenseCount: number;
  mrrEuros: number;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  contactJobTitle: string;
  contactUsername: string;
};

type SuperadminPlanSection = {
  key: string;
  name: string;
  description: string;
  capabilityLabels: string[];
  restrictionLabels: string[];
  accessScope: string;
  unitPriceEuros: number | null;
  subscriptions: SuperadminPlanSubscription[];
  subscribedCompanyCount: number;
  totalLicenses: number;
  totalMrrEuros: number;
};

const PLAN_PRESENTATION: Record<string, { description: string; restrictions: string[]; accessScope: string }> = {
  starter: {
    description: 'Plan d entree pour produire et ameliorer des scenarios avec un cadre structure.',
    restrictions: [
      'Pas d acces au workspace d implementation',
      'Pas d acces au module d audit',
      'Attribution nominative obligatoire a un utilisateur final',
    ],
    accessScope: 'Concu pour les contributeurs qui generent et ameliorent des scenarios sans piloter l implementation.',
  },
  builder: {
    description: 'Plan de production qui ajoute le workspace d implementation aux capacites Starter.',
    restrictions: [
      'Pas d acces au module d audit avance',
      'Attribution nominative obligatoire a un utilisateur final',
      'Une licence consommee par utilisateur assigne',
    ],
    accessScope: 'Concu pour les profils qui redigent, ameliorent puis implementent les scenarios dans le flux delivery.',
  },
  guardian: {
    description: 'Plan complet qui ajoute l audit, le reporting qualite et la gouvernance aux capacites Builder.',
    restrictions: [
      'Attribution nominative obligatoire a un utilisateur final',
      'Une licence consommee par utilisateur assigne',
      'Reserve aux usages necessitant audit, controle et pilotage qualite',
    ],
    accessScope: 'Concu pour les profils qui ont besoin de la chaine complete, jusqu a l audit et au reporting QA.',
  },
};

@Component({
  selector: 'app-backoffice-pricing-page',
  standalone: true,
  imports: [CommonModule, CurrencyPipe],
  templateUrl: './pricing-page.component.html',
  styleUrl: './pricing-page.component.css'
})
export class BackofficePricingPageComponent implements OnInit {
  private readonly backofficeService = inject(BackofficeService);
  private readonly currencyFormatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });

  readonly overview = this.backofficeService.overview;
  readonly plans = this.backofficeService.planOptions;
  readonly planFeatureCatalog = this.backofficeService.planFeatureCatalog;
  readonly currentCompanyId = this.backofficeService.currentCompanyId;
  readonly isSuperadmin = computed(() => this.overview()?.scope === 'superadmin');
  readonly companyAdminViewMode = signal<'detailed' | 'compact'>('detailed');
  readonly companies = computed(() => {
    const companies = [...this.backofficeService.companies()];
    return this.isSuperadmin()
      ? companies.sort((a, b) => Number(b.monthlyTotalEuros || 0) - Number(a.monthlyTotalEuros || 0))
      : companies.sort((a, b) => a.name.localeCompare(b.name));
  });
  readonly currentCompany = computed(() =>
    this.companies().find((company) => company.id === this.currentCompanyId()) ?? this.companies()[0] ?? null
  );
  readonly subscribedCompanies = computed(() =>
    this.companies().filter((company) => this.companyHasActiveSubscription(company))
  );

  readonly pageEyebrow = computed(() => this.isSuperadmin() ? 'Chiffrage' : 'Licences');
  readonly pageTitle = computed(() => this.isSuperadmin() ? 'Pilotage revenus et abonnements' : 'Pilotage des licences de votre compagnie');
  readonly pageDescription = computed(() => this.isSuperadmin()
    ? 'Vue superadmin du catalogue actif, des souscriptions facturees et du revenu mensuel recurrent.'
    : 'Vue operationnelle des plans souscrits, des licences allouees et des capacites incluses par plan.');

  readonly superadminPlanSections = computed<SuperadminPlanSection[]>(() => {
    if (!this.isSuperadmin()) {
      return [];
    }

    return this.plans().map((plan) => {
      const presentation = this.resolvePlanPresentation(plan);
      const featureLabels = this.buildCapabilityLabels(plan);
      const restrictionLabels = [...presentation.restrictions];
      const unitPriceEuros = plan.monthlyPriceEuros;
      const subscriptions = this.buildSuperadminPlanSubscriptions(plan);
      const totalLicenses = subscriptions.reduce((sum, subscription) => sum + subscription.licenseCount, 0);
      const totalMrrEuros = subscriptions.reduce((sum, subscription) => sum + subscription.mrrEuros, 0);

      return {
        key: plan.key,
        name: plan.name,
        description: presentation.description,
        capabilityLabels: featureLabels,
        restrictionLabels,
        accessScope: presentation.accessScope,
        unitPriceEuros,
        subscriptions,
        subscribedCompanyCount: subscriptions.length,
        totalLicenses,
        totalMrrEuros,
      };
    });
  });

  readonly visiblePlanCards = computed<CompanyAdminPlanCard[]>(() => {
    if (this.isSuperadmin()) {
      return [];
    }

    const company = this.currentCompany();
    if (!company) {
      return [];
    }

    return this.plans()
      .filter((plan) => this.isPlanVisibleForCompany(company, plan.key))
      .map((plan) => {
        const total = this.readPlanValue(company, 'seatQuotaByPlan', plan.key);
        const used = this.readPlanValue(company, 'usedSeatsByPlan', plan.key);
        const remaining = this.readPlanValue(company, 'remainingSeatsByPlan', plan.key);
        const presentation = this.resolvePlanPresentation(plan);

        return {
          key: plan.key,
          name: plan.name,
          description: presentation.description,
          capabilityLabels: this.buildCapabilityLabels(plan),
          restrictionLabels: presentation.restrictions,
          accessScope: presentation.accessScope,
          total,
          used,
          remaining,
          unitPriceEuros: null,
          revenueEuros: null,
        };
      })
      .filter((plan) => plan.total > 0 || plan.used > 0 || plan.remaining > 0);
  });

  readonly totalPurchasedLicenses = computed(() =>
    this.visiblePlanCards().reduce((sum, plan) => sum + plan.total, 0)
  );
  readonly allocatedLicenses = computed(() =>
    this.visiblePlanCards().reduce((sum, plan) => sum + plan.used, 0)
  );
  readonly remainingLicenses = computed(() =>
    this.visiblePlanCards().reduce((sum, plan) => sum + plan.remaining, 0)
  );
  readonly activePlanCount = computed(() => this.visiblePlanCards().length);
  readonly superadminActivePlanCount = computed(() => this.superadminPlanSections().length);
  readonly superadminCustomerCompanyCount = computed(() => this.subscribedCompanies().length);
  readonly totalSoldSeats = computed(() =>
    this.superadminPlanSections().reduce((sum, plan) => sum + plan.totalLicenses, 0)
  );
  readonly globalMonthlyRecurringRevenue = computed(() =>
    this.superadminPlanSections().reduce((sum, plan) => sum + plan.totalMrrEuros, 0)
  );

  ngOnInit(): void {
    this.backofficeService.loadOverview();
  }

  setCompanyAdminViewMode(mode: 'detailed' | 'compact'): void {
    this.companyAdminViewMode.set(mode);
  }

  trackPlan(_: number, plan: CompanyAdminPlanCard): string {
    return plan.key;
  }

  trackPlanSection(_: number, plan: SuperadminPlanSection): string {
    return plan.key;
  }

  trackPlanSubscription(_: number, subscription: SuperadminPlanSubscription): string {
    return subscription.companyId;
  }

  trackCompany(_: number, company: BackofficeCompany): string {
    return company.id;
  }

  companySubscriptionDetails(company: BackofficeCompany): CompanySubscriptionDetail[] {
    return this.plans()
      .map((plan) => {
        const seatCount = this.readPlanValue(company, 'seatQuotaByPlan', plan.key);
        if (seatCount <= 0) {
          return null;
        }

        return {
          key: plan.key,
          name: plan.name,
          seatCount,
        };
      })
      .filter((plan): plan is CompanySubscriptionDetail => plan !== null)
      .sort((left, right) => right.seatCount - left.seatCount || left.name.localeCompare(right.name));
  }

  companyTotalLicenses(company: BackofficeCompany): number {
    return this.plans()
      .reduce((sum, plan) => sum + this.readPlanValue(company, 'seatQuotaByPlan', plan.key), 0);
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '—';
    }

    return this.currencyFormatter.format(Number(value));
  }

  private buildCapabilityLabels(plan: BackofficePlan): string[] {
    const labelsByKey = new Map(
      this.planFeatureCatalog().map((feature) => [feature.key, feature.label])
    );
    return plan.features.map((feature) => labelsByKey.get(feature) || feature);
  }

  private buildSuperadminPlanSubscriptions(plan: BackofficePlan): SuperadminPlanSubscription[] {
    const unitPriceEuros = Number(plan.monthlyPriceEuros || 0);

    return this.subscribedCompanies()
      .map((company) => {
        const licenseCount = this.readPlanValue(company, 'seatQuotaByPlan', plan.key);
        if (licenseCount <= 0) {
          return null;
        }

        return {
          companyId: company.id,
          companyName: company.name,
          licenseCount,
          mrrEuros: licenseCount * unitPriceEuros,
          contactFirstName: this.formatContactValue(company.contact?.firstName),
          contactLastName: this.formatContactValue(company.contact?.lastName),
          contactEmail: this.formatContactValue(company.contact?.email),
          contactPhone: this.formatContactValue(company.contact?.phone),
          contactJobTitle: this.formatContactValue(company.contact?.jobTitle),
          contactUsername: this.formatContactValue(company.contact?.username),
        };
      })
      .filter((subscription): subscription is SuperadminPlanSubscription => subscription !== null)
      .sort((left, right) => right.mrrEuros - left.mrrEuros || left.companyName.localeCompare(right.companyName));
  }

  private resolvePlanPresentation(plan: BackofficePlan): { description: string; restrictions: string[]; accessScope: string } {
    return PLAN_PRESENTATION[plan.key] || {
      description: plan.description,
      restrictions: ['Attribution nominative obligatoire a un utilisateur final.'],
      accessScope: 'Acces produit determine par les fonctionnalites incluses dans ce plan.',
    };
  }

  private companyHasActiveSubscription(company: BackofficeCompany): boolean {
    return this.plans().some((plan) => this.readPlanValue(company, 'seatQuotaByPlan', plan.key) > 0);
  }

  private isPlanVisibleForCompany(company: BackofficeCompany | null, planKey: string): boolean {
    if (!company) {
      return false;
    }

    return company.selectedPlanKeys.includes(planKey)
      || this.readPlanValue(company, 'seatQuotaByPlan', planKey) > 0
      || this.readPlanValue(company, 'usedSeatsByPlan', planKey) > 0
      || this.readPlanValue(company, 'remainingSeatsByPlan', planKey) > 0;
  }

  private readPlanValue(
    company: BackofficeCompany,
    field: 'seatQuotaByPlan' | 'usedSeatsByPlan' | 'remainingSeatsByPlan',
    planKey: string
  ): number {
    return Number(company[field]?.[planKey] || 0);
  }

  private formatContactValue(value: string | null | undefined): string {
    const normalized = String(value || '').trim();
    return normalized || '—';
  }
}
